/**
 * Client-side report persistence (localStorage).
 *
 * Free tier: max 3 saved reports (one slot per domain; re-auditing a domain
 * replaces its existing report).  If all 3 slots are taken by different
 * domains, the oldest report is evicted to make room for the new one.
 */

import type { AuditResponse, SavedAuditReport } from '../types';

const STORAGE_KEY = 'fixmyseo_audit_reports';
export const MAX_FREE_REPORTS = 3;

export function loadReports(): SavedAuditReport[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as SavedAuditReport[];
  } catch {
    return [];
  }
}

export function saveReport(domain: string, data: AuditResponse): SavedAuditReport[] {
  let reports = loadReports();

  const newEntry: SavedAuditReport = {
    id: crypto.randomUUID(),
    domain,
    savedAt: new Date().toISOString(),
    data,
  };

  // Replace existing report for the same domain (keep only the latest per domain)
  const existingIdx = reports.findIndex((r) => r.domain === domain);
  if (existingIdx >= 0) {
    reports[existingIdx] = newEntry;
  } else {
    // Evict oldest if at cap
    if (reports.length >= MAX_FREE_REPORTS) {
      reports.sort(
        (a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime(),
      );
      reports = reports.slice(0, MAX_FREE_REPORTS - 1);
    }
    reports.unshift(newEntry);
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(reports));
  } catch {
    // Storage quota exceeded — silently continue
  }
  return reports;
}

export function deleteReport(id: string): SavedAuditReport[] {
  const reports = loadReports().filter((r) => r.id !== id);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(reports));
  } catch {
    // ignore
  }
  return reports;
}
